"use client";

import React, { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { ArrowRight, Check, ChevronUp, Layers, PlayCircle, Sparkles } from "lucide-react";

import { ScrollReveal } from "@/app/components/marketing/ScrollReveal";
import {
  ADVISOR_AUTH_PRIMARY_CTA_GRADIENT,
  buildDemoBookingMailto,
  buildPrimaryTrialCtaLabel,
} from "@/app/components/marketing/marketing-constants";
import { buildPrihlaseniWithPlanIntent } from "@/lib/billing/plan-intent";
import {
  COVERAGE_BADGE_CLASS,
  COVERAGE_ITEMS,
} from "@/data/marketing-coverage";

export type MarketingCoverageSurface = "dark" | "light";

export type MarketingCoverageHeading = {
  /** `null` / prázdné = bez eyeb řádku */
  eyebrow?: string | null;
  title: string;
  lead: React.ReactNode;
};

const defaultCoverageLead = (surface: MarketingCoverageSurface) => (
  <>
    CRM propojuje klienty, dokumenty, e-maily, úkoly a obchody. U každé položky je stav:{" "}
    <span className={surface === "dark" ? "text-slate-300" : "text-slate-800"}>Dostupné</span>,{" "}
    <span className={surface === "dark" ? "text-slate-300" : "text-slate-800"}>Připravujeme</span> nebo{" "}
    <span className={surface === "dark" ? "text-slate-300" : "text-slate-800"}>Ve výhledu</span> — podle reálného stavu produktu.
  </>
);

export function MarketingCoverageSection({
  surface = "dark",
  trialDaysLabel,
  ctaVariant = "home",
  heading,
  sectionId = "vyhody",
  showFooterCta = true,
  showReveal = true,
}: {
  surface?: MarketingCoverageSurface;
  trialDaysLabel: string;
  /** „tarify“ = primární trial + odkaz na ukázky místo demo mailu. */
  ctaVariant?: "home" | "tarify";
  heading?: MarketingCoverageHeading;
  /** Výchozí `vyhody` (homepage); na `/tarify` např. `pokryti`. */
  sectionId?: string;
  showFooterCta?: boolean;
  showReveal?: boolean;
}) {
  const [isCoverageRevealed, setIsCoverageRevealed] = useState(() => !showReveal);
  const demoMailto = buildDemoBookingMailto();

  const shell =
    surface === "dark"
      ? {
          section: "relative z-10 scroll-mt-24 overflow-hidden px-5 py-20 md:px-6 md:py-28",
          blobB: "absolute bottom-0 left-0 h-[520px] w-[520px] rounded-full bg-emerald-600/5 blur-[100px]",
          blobT: "absolute right-0 top-0 h-[560px] w-[560px] rounded-full bg-indigo-600/10 blur-[100px]",
          eyebrowWrap: "mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2",
          eyebrowText: "text-xs font-bold uppercase tracking-widest text-slate-300",
          title: "mb-6 font-jakarta text-4xl font-extrabold tracking-tight text-white md:text-5xl",
          lead: "text-lg font-medium leading-relaxed text-slate-400 md:text-xl",
          featureCard:
            "feature-card flex h-full flex-col rounded-3xl p-8 border border-white/[0.05] bg-white/[0.02] backdrop-blur-sm transition-all hover:border-indigo-400/30 hover:bg-white/[0.04]",
          cardTitle: "mb-4 font-jakarta text-xl font-bold leading-tight text-white",
          cardLead: "mb-4 text-sm font-medium leading-relaxed text-slate-400",
          bullet: "flex items-start gap-2.5 text-sm font-medium text-slate-400",
          revealBtn:
            "group relative flex items-center gap-3 rounded-full border border-white/10 bg-[#0b1021] px-8 py-5 font-jakarta text-lg font-bold text-white transition-all duration-300 hover:bg-white hover:text-[#0b1021] outline-none focus-visible:ring-2 focus-visible:ring-indigo-400/90 focus-visible:ring-offset-2 focus-visible:ring-offset-[#060918]",
          hideBtn:
            "inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-6 py-3 font-jakarta text-sm font-bold text-slate-400 transition-colors hover:bg-white/10 hover:text-white outline-none focus-visible:ring-2 focus-visible:ring-indigo-400/90 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0b1021]",
          ctaPanel:
            "relative mx-auto max-w-4xl overflow-hidden rounded-[2.5rem] border border-indigo-500/20 bg-gradient-to-br from-indigo-900/40 to-[#0b1021] p-10 text-center shadow-2xl shadow-indigo-900/20 md:p-14",
          ctaHeading: "mb-8 font-jakarta text-2xl font-extrabold text-white md:text-3xl",
          secondaryCta:
            "flex w-full items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-8 py-4 font-jakarta text-base font-bold text-white transition-all hover:bg-white/10 sm:w-auto",
        }
      : {
          section: "relative z-10 scroll-mt-24 overflow-hidden px-5 py-20 md:px-6 md:py-28 bg-[#F5F3FB]",
          blobB: "absolute bottom-0 left-0 h-[520px] w-[520px] rounded-full bg-emerald-500/10 blur-[100px]",
          blobT: "absolute right-0 top-0 h-[560px] w-[560px] rounded-full bg-indigo-500/15 blur-[100px]",
          eyebrowWrap: "mb-6 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 shadow-sm",
          eyebrowText: "text-xs font-bold uppercase tracking-widest text-slate-600",
          title: "mb-6 font-jakarta text-4xl font-extrabold tracking-tight text-slate-950 md:text-5xl",
          lead: "text-lg font-medium leading-relaxed text-slate-600 md:text-xl",
          featureCard:
            "flex h-full flex-col rounded-3xl border border-slate-200 bg-white p-8 shadow-[0_16px_50px_rgba(15,23,42,0.06)] transition-all hover:border-indigo-200 hover:shadow-[0_20px_60px_rgba(79,70,229,0.08)]",
          cardTitle: "mb-4 font-jakarta text-xl font-bold leading-tight text-slate-950",
          cardLead: "mb-4 text-sm font-medium leading-relaxed text-slate-600",
          bullet: "flex items-start gap-2.5 text-sm font-medium text-slate-600",
          revealBtn:
            "group relative flex items-center gap-3 rounded-full border border-slate-200 bg-white px-8 py-5 font-jakarta text-lg font-bold text-slate-950 shadow-sm transition-all duration-300 hover:border-indigo-300 hover:bg-indigo-50 outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/80 focus-visible:ring-offset-2 focus-visible:ring-offset-[#F5F3FB]",
          hideBtn:
            "inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-6 py-3 font-jakarta text-sm font-bold text-slate-600 shadow-sm transition-colors hover:border-indigo-200 hover:text-indigo-800 outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/80 focus-visible:ring-offset-2 focus-visible:ring-offset-[#F5F3FB]",
          ctaPanel:
            "relative mx-auto max-w-4xl overflow-hidden rounded-[2.5rem] border border-indigo-200 bg-gradient-to-br from-indigo-50 to-white p-10 text-center shadow-[0_24px_80px_rgba(15,23,42,0.08)] md:p-14",
          ctaHeading: "mb-8 font-jakarta text-2xl font-extrabold text-slate-950 md:text-3xl",
          secondaryCta:
            "flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-8 py-4 font-jakarta text-base font-bold text-slate-950 transition-all hover:border-indigo-200 hover:bg-indigo-50 sm:w-auto",
        };

  const h: MarketingCoverageHeading =
    heading ?? {
      eyebrow: "Integrace a plán vývoje",
      title: "Co Aidvisora pokrývá",
      lead: defaultCoverageLead(surface),
    };

  return (
    <section id={sectionId} className={shell.section}>
      <div className={`${shell.blobB} pointer-events-none`} aria-hidden />
      <div className={`${shell.blobT} pointer-events-none`} aria-hidden />
      <div className="relative z-10 mx-auto max-w-[1400px]">
        <ScrollReveal>
          <div className="mx-auto mb-16 max-w-3xl text-center">
            {h.eyebrow ? (
              <div className={shell.eyebrowWrap}>
                <Sparkles size={14} className={surface === "dark" ? "text-indigo-400" : "text-indigo-600"} />
                <span className={shell.eyebrowText}>{h.eyebrow}</span>
              </div>
            ) : null}
            <h2 className={shell.title}>{h.title}</h2>
            <p className={shell.lead}>{h.lead}</p>
          </div>
        </ScrollReveal>

        {showReveal ? (
        <div
          className={`text-center transition-all duration-500 ease-in-out ${
            isCoverageRevealed ? "mb-0 h-0 scale-95 overflow-hidden opacity-0" : "mb-20 h-auto scale-100 opacity-100 md:mb-24"
          }`}
        >
          <div className={surface === "dark" ? "glow-btn-wrapper" : ""}>
            <button
              type="button"
              onClick={() => setIsCoverageRevealed(true)}
              className={`${shell.revealBtn} inline-flex w-full max-w-xl justify-center sm:w-auto`}
            >
              <Layers className="text-indigo-500 transition-transform group-hover:rotate-12 group-hover:text-indigo-600" size={24} />
              Zobrazit přehled integrací
            </button>
          </div>
        </div>
        ) : null}

        <div
          className={`grid transition-[grid-template-rows,opacity] duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${
            isCoverageRevealed ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
          }`}
        >
          <div className="overflow-hidden">
            <div className="grid grid-cols-1 gap-6 pb-12 pt-4 md:grid-cols-2 md:gap-8 lg:grid-cols-3">
              {COVERAGE_ITEMS.map((feature) => {
                const Icon = feature.icon;
                const bullets = feature.bullets ?? [];
                return (
                  <div key={feature.id} className={shell.featureCard}>
                    <div className="mb-6 flex items-start justify-between gap-4">
                      {feature.logos && feature.logos.length > 0 ? (
                        <div className="flex shrink-0 items-center gap-2">
                          {feature.logos.map((logo) => (
                            <div
                              key={logo.src}
                              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-white/90 bg-white p-2 shadow-sm"
                            >
                              <Image
                                src={logo.src}
                                alt={logo.alt}
                                width={32}
                                height={32}
                                loading="lazy"
                                unoptimized={logo.src.endsWith(".svg")}
                                className="h-8 w-8 object-contain"
                              />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-white/90 bg-white p-2 shadow-sm">
                          {feature.logoSrc ? (
                            <Image
                              src={feature.logoSrc}
                              alt={feature.logoAlt ?? feature.title}
                              width={32}
                              height={32}
                              loading="lazy"
                              unoptimized={
                                feature.logoSrc.endsWith(".webp") || feature.logoSrc.endsWith(".svg")
                              }
                              className="h-8 w-8 object-contain"
                            />
                          ) : (
                            <Icon className="text-indigo-600" size={24} strokeWidth={2} aria-hidden />
                          )}
                        </div>
                      )}
                      <span
                        className={`whitespace-nowrap rounded-full border px-3 py-1 font-jakarta text-[10px] font-bold uppercase tracking-widest ${COVERAGE_BADGE_CLASS[feature.badge]}`}
                      >
                        {feature.badge}
                      </span>
                    </div>

                    <div>
                      <h3 className={shell.cardTitle}>{feature.title}</h3>
                      {feature.lead ? <p className={shell.cardLead}>{feature.lead}</p> : null}
                      {bullets.length > 0 ? (
                        <ul className="space-y-3">
                          {bullets.map((bullet) => (
                            <li key={bullet} className={shell.bullet}>
                              <Check size={16} strokeWidth={2.5} className="mt-0.5 shrink-0 text-indigo-500" />
                              <span className="leading-snug">{bullet}</span>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>

            {showReveal ? (
            <div className="pb-20 text-center md:pb-24">
              <button type="button" onClick={() => setIsCoverageRevealed(false)} className={shell.hideBtn}>
                <ChevronUp size={16} />
                Skrýt další funkce
              </button>
            </div>
            ) : null}
          </div>
        </div>

        {showFooterCta ? (
        <ScrollReveal delay={100}>
          <div className={shell.ctaPanel}>
            <div className={surface === "dark" ? "relative z-10" : ""}>
              <h3 className={shell.ctaHeading}>Chcete funkce ověřit na vlastním účtu?</h3>
              <div className="flex flex-col items-center justify-center gap-4 md:flex-row md:gap-6">
                {ctaVariant === "tarify" ? (
                  <>
                    <Link
                      href={buildPrihlaseniWithPlanIntent("pro")}
                      className={`flex w-full items-center justify-center gap-2 rounded-2xl px-8 py-4 font-jakarta text-base font-bold transition-all hover:scale-105 sm:w-auto ${ADVISOR_AUTH_PRIMARY_CTA_GRADIENT}`}
                    >
                      Začít {trialDaysLabel} zdarma <ArrowRight size={18} />
                    </Link>
                    <Link href="/#showcase" className={shell.secondaryCta}>
                      <PlayCircle size={18} className="text-indigo-500" />
                      Podívat se na ukázky z aplikace
                    </Link>
                  </>
                ) : (
                  <>
                    <Link
                      href="/prihlaseni?register=1"
                      className={`flex w-full items-center justify-center gap-2 rounded-2xl px-8 py-4 font-jakarta text-base font-bold transition-all hover:scale-[1.02] sm:w-auto ${ADVISOR_AUTH_PRIMARY_CTA_GRADIENT}`}
                    >
                      {buildPrimaryTrialCtaLabel(trialDaysLabel)} <ArrowRight size={18} />
                    </Link>
                    <a href={demoMailto} className={shell.secondaryCta}>
                      Domluvit demo
                    </a>
                  </>
                )}
              </div>
            </div>
          </div>
        </ScrollReveal>
        ) : null}
      </div>
    </section>
  );
}
